// A RATE ON A SCHEDULE ACTIVITY - everything filed under it is priced by it.
//
// Owner, 2026-10-26: "in contractor hours and cost, add the option to override
// by activity in the schedule, that the issue or shop drawings are linked to".
//
// WHERE IT SITS, and the window says so rather than leaving it to be discovered:
//   a line's own rate  >  THIS  >  the contractor's project rate  >  account rate
// Most specific first. An issue can sit on several activities; its PRIMARY one
// decides, which is the rule the server applies (models/activityRateOverride).
//
// Its own file because RateOverrides is already long and the issues page is past
// the hooks lint's path budget - see feedback_hooks_lint_path_limit.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Input, InputNumber, Popconfirm, Select, Table, Tag, Tooltip, message } from "antd";
import ActivityPicker from "components/Coordination/ActivityPicker";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { AUDIENCE_LABEL, WhoSeesRate } from "./rateAudience";
import { getActivityRates, setActivityRate, removeActivityRate } from "api/billableHours";
import { url, tokenRef } from "api";

const T = { muted: "#667085" };
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ActivityRates({ projectId, contractors = [], canEdit }) {
  const [rows, setRows] = useState([]);
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ ganttItemId: null, contractorCompanyId: "", rate: null, appliesTo: "owner", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!projectId) { setRows([]); return; }
    setLoading(true);
    getActivityRates(projectId)
      .then((d) => setRows(Array.isArray(d && d.rates) ? d.rates : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(load, [load]);

  // The project's schedule, for the picker. A rate can name any row - a level or
  // a package as readily as a single activity - because that is what the schedule
  // tree offers and what "by activity" means to somebody reading it.
  useEffect(() => {
    let alive = true;
    if (!projectId) { setPlan([]); return undefined; }
    fetch(`${url}/projects/${projectId}/gantt`, { headers: { Authorization: `Bearer ${tokenRef.current}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((feed) => { if (alive) setPlan(withActivityPaths(feed.items || [])); })
      .catch(() => { if (alive) setPlan([]); });
    return () => { alive = false; };
  }, [projectId]);

  const nameOf = useMemo(() => {
    const m = new Map();
    for (const c of contractors) m.set(String(c.companyId), c.companyName || c.label || c.companyId);
    return m;
  }, [contractors]);

  const save = () => {
    if (!draft.ganttItemId) return message.warning("Pick the schedule activity the rate applies to");
    if (draft.rate == null || draft.rate === "") return message.warning("Give an hourly rate");
    setSaving(true);
    setActivityRate({
      projectId,
      ganttItemId: draft.ganttItemId,
      contractorCompanyId: draft.contractorCompanyId || undefined,
      contractorName: nameOf.get(String(draft.contractorCompanyId)) || "",
      rate: draft.rate,
      appliesTo: draft.appliesTo,
      reason: draft.reason,
    })
      .then(() => {
        message.success("Activity rate saved");
        setDraft({ ganttItemId: null, contractorCompanyId: "", rate: null, appliesTo: "owner", reason: "" });
        load();
      })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not save the rate"))
      .finally(() => setSaving(false));
  };

  const columns = [
    { title: "Activity", dataIndex: "activityLabel", render: (v, r) => v || <Tooltip title="The schedule row was removed; the rate still prices what is filed under it"><Tag>row removed</Tag></Tooltip> },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      render: (v, r) => (r.contractorCompanyId
        ? (v || nameOf.get(String(r.contractorCompanyId)) || r.contractorCompanyId)
        : <Tooltip title="Every contractor's hours under this activity"><Tag color="blue">Anyone</Tag></Tooltip>),
    },
    { title: "Rate", dataIndex: "rate", align: "right", render: (v) => <b>{money(v)}/h</b> },
    { title: "Who sees it", dataIndex: "appliesTo", render: (v) => <Tag color={v === "everyone" ? "gold" : undefined}>{AUDIENCE_LABEL[v] || v}</Tag> },
    { title: "Why", dataIndex: "reason", render: (v) => <span style={{ color: T.muted }}>{v || ""}</span> },
    ...(canEdit ? [{
      title: "",
      key: "x",
      width: 80,
      render: (_, r) => (
        <Popconfirm
          title="Remove this activity rate?"
          description="The lines under it go back to the contractor's project or account rate."
          onConfirm={() => removeActivityRate(r._id).then(() => { message.success("Removed"); load(); })
            .catch((e) => message.error((e && (e.err || e.message)) || "Could not remove it"))}
        >
          <Button size="small" danger type="link">Remove</Button>
        </Popconfirm>
      ),
    }] : []),
  ];

  if (!projectId) {
    return <Alert type="info" showIcon message="Pick a project to set rates on its schedule activities." />;
  }

  return (
    <Card size="small" title="Rates by schedule activity" style={{ marginTop: 12 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 10 }}
        message="A line's own rate still wins"
        description="An activity rate prices every issue and shop drawing filed under that activity, unless the line has a rate of its own. It beats the contractor's project and account rates. An issue linked to several activities is priced by its first one."
      />
      {canEdit && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 10 }}>
          <ActivityPicker
            items={plan}
            value={draft.ganttItemId || undefined}
            onChange={(v) => setDraft((d) => ({ ...d, ganttItemId: v }))}
            placeholder="Schedule activity or category"
            style={{ minWidth: 280 }}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 200 }}
            placeholder="Any contractor"
            value={draft.contractorCompanyId || undefined}
            onChange={(v) => setDraft((d) => ({ ...d, contractorCompanyId: v || "" }))}
            options={contractors.map((c) => ({ value: c.companyId, label: c.companyName || c.label || c.companyId }))}
          />
          <InputNumber
            min={0}
            max={10000}
            step={5}
            addonBefore="$"
            addonAfter="/h"
            placeholder="rate"
            value={draft.rate}
            onChange={(v) => setDraft((d) => ({ ...d, rate: v }))}
            style={{ width: 170 }}
          />
          <WhoSeesRate value={draft.appliesTo} onChange={(v) => setDraft((d) => ({ ...d, appliesTo: v }))} />
          <Input
            placeholder="Why (optional)"
            value={draft.reason}
            onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
            style={{ width: 220 }}
            maxLength={500}
          />
          <Button type="primary" loading={saving} onClick={save} data-testid="save-activity-rate">Save rate</Button>
        </div>
      )}
      <Table
        size="small"
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: "No activity rates on this project yet." }}
      />
    </Card>
  );
}
